(function () {
  /**
   * Applies the custom height from section data attributes to the Flickity viewport.
   * This function is crucial for overriding Flickity's default height management,
   * especially when 'setGallerySize' is false.
   * @param {HTMLElement} sectionEl - The DOM element of the Shopify section.
   * @param {Flickity} [flickityInstance] - Optional: The active Flickity instance.
   */
  function applyCustomHeight(sectionEl, flickityInstance) {
    var viewport = sectionEl.querySelector(".flickity-viewport");

    if (
      !viewport ||
      !sectionEl.dataset.slideHeightMobile ||
      !sectionEl.dataset.slideHeightDesktop
    ) {
      console.warn(
        "applyCustomHeight: Aborting - Missing viewport or height data attributes for section:",
        sectionEl.dataset.sectionId
      );
      return;
    }

    var mobileH = sectionEl.dataset.slideHeightMobile + "px";
    var desktopH = sectionEl.dataset.slideHeightDesktop + "px";
    var newHeight;

    if (window.matchMedia("(min-width: 750px)").matches) {
      newHeight = desktopH;
    } else {
      newHeight = mobileH;
    }

    // Use setProperty to apply the height with '!important', ensuring it overrides other styles.
    viewport.style.setProperty("height", newHeight, "important");

    if (flickityInstance && flickityInstance.resize) {
      setTimeout(function () {
        flickityInstance.resize();
      }, 55); // Small delay to allow DOM to update before Flickity resizes.
    }
  }

  /**
   * Initializes or re-initializes a Flickity banner instance within a given section element.
   * Handles destroying previous instances and setting up options.
   * @param {HTMLElement} sectionEl - The DOM element of the Shopify section.
   * @param {string} [targetBlockId] - Optional: The ID of a specific block (slide) to select on load/interaction.
   */
  function initBanner(sectionEl, targetBlockId) {
    if (!window.Flickity || !sectionEl) {
      console.warn(
        "Flickity library or Section Element not found. Skipping initialization."
      );
      return;
    }

    var sectionId = sectionEl.dataset.sectionId;
    var carousel = sectionEl.querySelector("#flickity-carousel-" + sectionId);

    if (!carousel) {
      console.warn(
        "Flickity carousel element not found in section:",
        sectionId,
        "(Expected #flickity-carousel-" + sectionId + ")"
      );
      return;
    }

    // Destroy any existing Flickity instance on this carousel to prevent conflicts
    var currentFlkty = Flickity.data(carousel);
    if (currentFlkty) {
      currentFlkty.destroy();
    }

    var opts = {};
    if (carousel.dataset.flickityOptions) {
      try {
        opts = JSON.parse(carousel.dataset.flickityOptions);
      } catch (e) {
        console.error(
          "Error parsing Flickity options for carousel in section:",
          sectionId,
          e,
          carousel.dataset.flickityOptions
        );
        // Provide sensible defaults if options are corrupt
        opts = {
          cellAlign: "left",
          contain: true,
          adaptiveHeight: false,
          setGallerySize: false,
        };
      }
    } else {
      console.warn(
        "No flickityOptions data attribute found on carousel in section:",
        sectionId,
        ". Using defaults."
      );
      opts = {
        cellAlign: "left",
        contain: true,
        adaptiveHeight: false,
        setGallerySize: false,
      };
    }

    opts.on = opts.on || {}; // Ensure 'on' object exists for event handlers
    let initialIndex = 0;

    // Determine initial slide index if a targetBlockId is provided
    if (targetBlockId) {
      const slides = carousel.querySelectorAll(".carousel-cell");
      for (let i = 0; i < slides.length; i++) {
        if (slides[i].id && slides[i].id.endsWith("-" + targetBlockId)) {
          initialIndex = i;
          break;
        }
      }
    }
    opts.initialIndex = initialIndex; // Set initialIndex for Flickity to handle

    // Flickity event handlers
    opts.on.ready = function () {
      // 'this' refers to the Flickity instance here
      var flickityInstance = this;
      var shopifySectionWrapper =
        sectionEl.closest(".shopify-section") || sectionEl;

      shopifySectionWrapper.classList.add("section-loaded"); // For FOUC prevention
      carousel.style.visibility = "visible"; // Make carousel visible once ready

      applyCustomHeight(sectionEl, flickityInstance); // Apply custom height after Flickity initializes
    };

    opts.on.error = function () {
      console.error(
        "Flickity initialization error occurred for carousel in section:",
        sectionId
      );
      // Still ensure visibility and FOUC class if an error happens
      carousel.style.visibility = "visible";
      var shopifySectionWrapper =
        sectionEl.closest(".shopify-section") || sectionEl;
      shopifySectionWrapper.classList.add("section-loaded");
    };

    var newFlktyInstance = new Flickity(carousel, opts);
  }

  /**
   * Finds all Flickity slideshow sections on the page and initializes them.
   */
  function initAllBanners() {
    document
      .querySelectorAll('[data-section-type="advanced-flickity-slideshow"]')
      .forEach(function (section) {
        initBanner(section);
      });
  }

  // Initialize banners on initial page load
  document.addEventListener("DOMContentLoaded", initAllBanners);

  // --- Shopify Theme Editor Event Listeners ---
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (event) {
      // Check if the loaded section is our Flickity banner type and matches the event's sectionId
      const sectionEl = event.target.querySelector(
        `[data-section-id="${event.detail.sectionId}"][data-section-type="advanced-flickity-slideshow"]`
      );
      if (sectionEl) {
        initBanner(sectionEl);
      }
    });

    document.addEventListener("shopify:block:select", function (event) {
      var wrapper = event.target.closest(
        '[data-section-type="advanced-flickity-slideshow"]'
      );
      if (wrapper) {
        // Re-initialize and select the targeted block
        initBanner(wrapper, event.detail.blockId);
      }
    });

    document.addEventListener("shopify:block:deselect", function (event) {
      var wrapper = event.target.closest(
        '[data-section-type="advanced-flickity-slideshow"]'
      );
      if (wrapper) {
        // Re-initialize without a specific target (defaults to first slide or last state)
        initBanner(wrapper);
      }
    });

    document.addEventListener("shopify:section:unload", function (event) {
      const sectionEl = event.target.querySelector(
        `[data-section-id="${event.detail.sectionId}"][data-section-type="advanced-flickity-slideshow"]`
      );
      if (sectionEl) {
        var carousel = sectionEl.querySelector(".main-carousel");
        var currentFlkty = carousel ? Flickity.data(carousel) : null;
        if (currentFlkty) {
          try {
            currentFlkty.destroy();
          } catch (err) {
            console.warn(
              "Error destroying Flickity on unload for section:",
              event.detail.sectionId,
              err
            );
          }
        }
      }
    });

    // Handles drag-and-drop reordering of sections in the editor
    document.addEventListener("shopify:section:reorder", function (event) {
      // It's simplest to re-initialize all banners as their order might have changed context
      initAllBanners();
    });

    // Handles changes to section settings in the editor
    let settingsChangeTimeout;
    document.addEventListener("shopify:setting:change", function (event) {
      clearTimeout(settingsChangeTimeout);
      settingsChangeTimeout = setTimeout(function () {
        const sectionEl = event.target.querySelector(
          `[data-section-id="${event.detail.sectionId}"][data-section-type="advanced-flickity-slideshow"]`
        );

        if (sectionEl) {
          const settings = event.detail.section_settings;
          // Update data attributes for height on the section element
          // This allows applyCustomHeight to pick up the new values when initBanner is called
          if (settings && settings.slide_height_desktop !== undefined) {
            sectionEl.dataset.slideHeightDesktop =
              settings.slide_height_desktop;
          }
          if (settings && settings.slide_height_mobile !== undefined) {
            sectionEl.dataset.slideHeightMobile = settings.slide_height_mobile;
          }

          // Re-init ensures all changes (like autoplay, dots, height via data attributes, etc.) are applied.
          initBanner(sectionEl);
        }
      }, 300); // Debounce setting changes to avoid rapid re-initializations
    });
  }

  // --- Window Resize Listener ---
  // Re-applies custom height and resizes Flickity on window resize
  var windowResizeTimeout;
  window.addEventListener("resize", function () {
    clearTimeout(windowResizeTimeout);
    windowResizeTimeout = setTimeout(function () {
      document
        .querySelectorAll('[data-section-type="advanced-flickity-slideshow"]')
        .forEach(function (sectionEl) {
          var carousel = sectionEl.querySelector(".main-carousel");
          var flickityInstance = carousel ? Flickity.data(carousel) : null;
          if (flickityInstance) {
            applyCustomHeight(sectionEl, flickityInstance);
          }
        });
    }, 250); // Debounce resize events
  });
})();
